import { writable, get } from 'svelte/store';
import { toast } from 'svelte-sonner';
import type { TaskCopyPreferences } from '../types';
import { jsonHeaders } from '../apiHelpers';
import { optimisticMutation } from '../optimisticMutation';

/**
 * Which fields the "Copy incomplete as…" actions include. Persisted per user
 * (users.preferences["task_copy"]) and delivered via Inertia shared props.
 */

export const DEFAULT_TASK_COPY_PREFERENCES: TaskCopyPreferences = {
  description: true,
  tags: true,
  priority: true,
  created_at: true,
  updated_at: true,
};

export const taskCopyPreferences = writable<TaskCopyPreferences>(DEFAULT_TASK_COPY_PREFERENCES);

// Last known server value. Page visits re-deliver the same shared prop, so only
// a changed server value overwrites the store (never a pending toggle). Saves
// update it too; otherwise another tab changing the setting back to the value
// this tab first loaded would look unchanged here and be skipped.
let lastServerValue = '';

function withDefaults(prefs: Partial<TaskCopyPreferences> | null | undefined): TaskCopyPreferences {
  return { ...DEFAULT_TASK_COPY_PREFERENCES, ...prefs };
}

/** Compare by value in a fixed field order, independent of key order. */
function serverKey(prefs: TaskCopyPreferences): string {
  return JSON.stringify(
    (Object.keys(DEFAULT_TASK_COPY_PREFERENCES) as (keyof TaskCopyPreferences)[]).map(
      (field) => prefs[field],
    ),
  );
}

export function initTaskCopyPreferences(prefs: Partial<TaskCopyPreferences> | null | undefined) {
  const next = withDefaults(prefs);
  const key = serverKey(next);
  if (key === lastServerValue) return;
  lastServerValue = key;
  taskCopyPreferences.set(next);
}

export async function setTaskCopyPreferences(changes: Partial<TaskCopyPreferences>): Promise<void> {
  const result = await optimisticMutation({
    getState: () => get(taskCopyPreferences),
    applyOptimistic: (prefs) => ({ ...prefs, ...changes }),
    syncState: (prefs) => taskCopyPreferences.set(prefs),
    apiCall: async () => {
      const res = await fetch('/preferences/task_copy', {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify(changes),
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = (await res.json()) as { taskCopyPreferences: TaskCopyPreferences };
      lastServerValue = serverKey(withDefaults(data.taskCopyPreferences));
    },
    // No onSuccess: with rapid toggles, an earlier response would briefly
    // revert a later optimistic change. The optimistic state is final.
  });
  if (!result.success) {
    // The rollback may have undone a later toggle that did save, so let the
    // next page visit re-sync from the server whatever it delivers.
    lastServerValue = '';
    toast.error('Could not save copy settings');
  }
}
